import { useEffect, useState } from 'react';
import { supabase, getLearner, updateLearnerProfile } from '../services/supabaseClient.js';

// ─────────────────────────────────────────────
// ACCOUNT PAGE — /account (auth-required; App.jsx redirects to /signin if no
// session). Five sections: Account Details, Subscription, Billing History,
// Notifications, Danger Zone. Billing reads/writes go through the server
// endpoints (Stripe secret key is server-only); profile + consent writes go
// straight to the learners row under RLS (auth.uid() = id).
// ─────────────────────────────────────────────

const C = {
  bg:     'var(--bg-primary)',
  panel:  '#ffffff',
  card:   '#ffffff',
  elev:   '#f8fafc',
  border: '#e5e7eb',
  borderM:'#e5e7eb',
  text:   '#1e1e2e',
  dim:    'var(--brand-slate)',
  muted:  'var(--brand-slate)',
  teal:   'var(--brand-amber)',      // primary accent → amber, matching the app
  tealText:'#ffffff',
  soft:   'rgba(217,119,6,0.10)',
  red:    'var(--brand-coral)',
  emerald:'var(--brand-emerald)',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtMoney(cents, currency = 'usd') {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

async function authToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}
async function postJSON(url, body) {
  const token = await authToken();
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
}

// ── shared styles ──
const sectionStyle = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
  boxShadow: 'var(--card-shadow)', padding: '20px 22px', marginBottom: 16,
};
const h2Style = { fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: -0.2, margin: '0 0 16px' };
const inputStyle = {
  padding: '10px 13px', borderRadius: 10, background: C.elev,
  border: `1px solid ${C.borderM}`, color: C.text, fontSize: 14, outline: 'none',
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const btnPrimary = {
  padding: '10px 18px', borderRadius: 10, background: C.teal, border: 'none',
  color: C.tealText, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 4px 14px rgba(217,119,6,0.24)',
};
const btnGhost = {
  padding: '8px 14px', borderRadius: 9, background: '#ffffff', border: `1px solid ${C.border}`,
  color: C.dim, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

// ─────────────────────────────────────────────
// 1. ACCOUNT DETAILS
// ─────────────────────────────────────────────
function EditableField({ label, value, onSave, type = 'text', autoComplete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value ?? '');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const save = async () => {
    setSaving(true); setErr(null);
    const error = await onSave(draft.trim());
    setSaving(false);
    if (error) { setErr(error); return; }
    setEditing(false);
  };

  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, minWidth: 90 }}>{label}</span>
        {!editing ? (
          <>
            <span style={{ flex: 1, fontSize: 14, color: C.text, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
            <button style={btnGhost} onClick={() => { setDraft(value ?? ''); setEditing(true); }}>Edit</button>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type={type} value={draft} autoComplete={autoComplete}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && save()} style={inputStyle} autoFocus />
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button style={btnGhost} onClick={() => { setEditing(false); setErr(null); }}>Cancel</button>
          </div>
        )}
      </div>
      {err && <p style={{ fontSize: 12, color: C.red, margin: '6px 0 0', textAlign: 'right' }}>{err}</p>}
    </div>
  );
}

function ChangePassword() {
  const [open, setOpen]       = useState(false);
  const [cur, setCur]         = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);
  const [err, setErr]         = useState(null);

  const submit = async () => {
    setErr(null); setMsg(null);
    if (next.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setErr('New password and confirmation do not match.'); return; }
    setBusy(true);
    // Verify the current password by re-authenticating (updateUser does not
    // check it). Use the AUTH email from the session, not learner.email — the
    // latter is editable on this page and can diverge from the login email.
    const { data: { user } } = await supabase.auth.getUser();
    const authEmail = user?.email;
    if (!authEmail) { setBusy(false); setErr('Could not verify your session. Please sign in again.'); return; }
    const { error: reauthErr } = await supabase.auth.signInWithPassword({ email: authEmail, password: cur });
    if (reauthErr) { setBusy(false); setErr('Current password is incorrect.'); return; }
    const { error: updErr } = await supabase.auth.updateUser({ password: next });
    setBusy(false);
    if (updErr) { setErr(updErr.message || 'Could not update password.'); return; }
    setMsg('Password updated.');
    setCur(''); setNext(''); setConfirm('');
    setOpen(false);
  };

  return (
    <div style={{ paddingTop: 14 }}>
      {!open ? (
        <button style={btnGhost} onClick={() => { setOpen(true); setMsg(null); }}>Change Password</button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
          <input type="password" placeholder="Current password" value={cur} autoComplete="current-password"
            onChange={e => setCur(e.target.value)} style={inputStyle} />
          <input type="password" placeholder="New password (min 8 chars)" value={next} autoComplete="new-password"
            onChange={e => setNext(e.target.value)} style={inputStyle} />
          <input type="password" placeholder="Confirm new password" value={confirm} autoComplete="new-password"
            onChange={e => setConfirm(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
              {busy ? 'Updating…' : 'Update Password'}
            </button>
            <button style={btnGhost} onClick={() => { setOpen(false); setErr(null); }}>Cancel</button>
          </div>
          {err && <p style={{ fontSize: 12, color: C.red, margin: 0 }}>{err}</p>}
        </div>
      )}
      {msg && <p style={{ fontSize: 12, color: C.emerald, margin: '8px 0 0' }}>{msg}</p>}
    </div>
  );
}

function AccountDetails({ learner, onPatch }) {
  return (
    <div style={sectionStyle}>
      <h2 style={h2Style}>Account Details</h2>
      <EditableField label="Full name" value={learner.name} autoComplete="name"
        onSave={v => onPatch({ name: v || null })} />
      <EditableField label="Email" value={learner.email} type="email" autoComplete="email"
        onSave={v => onPatch({ email: v })} />
      <EditableField label="Phone" value={learner.phone} type="tel" autoComplete="tel"
        onSave={v => onPatch({ phone: v || null })} />
      <ChangePassword />
    </div>
  );
}

// ─────────────────────────────────────────────
// 2. SUBSCRIPTION
// ─────────────────────────────────────────────
function Subscription() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await postJSON('/api/account', { action: 'summary' });
      if (cancelled) return;
      if (ok) setSummary(data); else setErr('Could not load subscription details.');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const openUrl = async (endpoint, body) => {
    setBusy(true); setErr(null);
    const { ok, data } = await postJSON(endpoint, body);
    setBusy(false);
    if (ok && data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    else setErr('Could not open billing. Please try again in a moment.');
  };

  const addCard      = () => openUrl('/api/create-checkout-session', { applyEarlyBirdCoupon: true });
  const managePortal = () => openUrl('/api/account', { action: 'portal' });
  const reactivate   = () => openUrl('/api/account', { action: 'reactivate' });

  const offerBox = { marginTop: 4, padding: '16px', background: C.soft, border: `1px solid ${C.teal}`, borderRadius: 12 };
  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 13, color: C.dim }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{value}</span>
    </div>
  );

  let body;
  if (loading) {
    body = <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Loading…</p>;
  } else if (!summary) {
    body = <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Subscription details unavailable.</p>;
  } else {
    const { status, trial_end, has_payment_method, amount, currency, next_billing_date } = summary;

    if (status === 'trialing' && !has_payment_method) {
      body = (
        <div style={offerBox}>
          <p style={{ fontSize: 14, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>
            Add a card now — $5 off your first month, then $21.99/month.
          </p>
          <p style={{ fontSize: 13, color: C.dim, margin: '0 0 14px', lineHeight: 1.5 }}>No charge today.</p>
          <button style={{ ...btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={addCard}>
            {busy ? 'Opening…' : 'Add Card — $5 Off First Month'}
          </button>
        </div>
      );
    } else if (status === 'trialing') {
      body = (
        <div>
          {row('Trial active', fmtDate(trial_end))}
          {row(`Your card will be charged ${fmtMoney(amount, currency)} on`, fmtDate(next_billing_date))}
          <button style={{ ...btnPrimary, marginTop: 14, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={managePortal}>
            {busy ? 'Opening…' : 'Manage Subscription'}
          </button>
        </div>
      );
    } else if (status === 'active') {
      body = (
        <div>
          {row('Plan', `${fmtMoney(amount, currency)} / ${summary.interval || 'month'}`)}
          {row('Next billing date', fmtDate(next_billing_date))}
          {row('Amount', fmtMoney(amount, currency))}
          <button style={{ ...btnPrimary, marginTop: 14, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={managePortal}>
            {busy ? 'Opening…' : 'Manage Subscription'}
          </button>
        </div>
      );
    } else if (status === 'past_due' || status === 'canceled') {
      body = (
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 12px' }}>Your access has paused.</p>
          <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={reactivate}>
            {busy ? 'Opening…' : 'Reactivate — $21.99/month'}
          </button>
        </div>
      );
    } else if (status === 'none') {
      // Free tier (default subscription_status for every new learner). Give them
      // a path to convert before they exhaust their 3 free lessons. `reactivate`
      // opens subscription-mode Checkout (creates the Stripe customer on the fly
      // for a never-subscribed learner) with the EARLYBIRD first-month discount —
      // the same conversion flow the trial-exhausted paywall uses.
      body = (
        <div style={offerBox}>
          <p style={{ fontSize: 14, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>
            You're on the Free plan — 3 free lessons.
          </p>
          <p style={{ fontSize: 13, color: C.dim, margin: '0 0 14px', lineHeight: 1.5 }}>
            Upgrade for unlimited lessons — $5 off your first month, then $21.99/month. Cancel any time.
          </p>
          <button style={{ ...btnPrimary, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={reactivate}>
            {busy ? 'Opening…' : 'Upgrade to Paid — $16.99 first month'}
          </button>
        </div>
      );
    } else {
      body = <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>No active subscription.</p>;
    }
  }

  return (
    <div style={sectionStyle}>
      <h2 style={h2Style}>Subscription</h2>
      {body}
      {err && <p style={{ fontSize: 12, color: C.red, margin: '10px 0 0' }}>{err}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
// 3. BILLING HISTORY
// ─────────────────────────────────────────────
function BillingHistory() {
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await postJSON('/api/account', { action: 'invoices' });
      if (cancelled) return;
      setInvoices(ok ? (data.invoices || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={sectionStyle}>
      <h2 style={h2Style}>Billing History</h2>
      {loading && <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Loading…</p>}
      {!loading && (!invoices || invoices.length === 0) && (
        <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>No billing history yet.</p>
      )}
      {!loading && invoices && invoices.length > 0 && (
        <div>
          {invoices.map(inv => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}`, gap: 12 }}>
              <span style={{ fontSize: 13, color: C.text, minWidth: 110 }}>{fmtDate(inv.date)}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmtMoney(inv.amount, inv.currency)}</span>
              <span style={{ fontSize: 12, color: C.dim, textTransform: 'capitalize', flex: 1, textAlign: 'center' }}>{inv.status}</span>
              {(inv.pdf || inv.url)
                ? <a href={inv.pdf || inv.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: C.teal, textDecoration: 'none' }}>Download Receipt ↗</a>
                : <span style={{ fontSize: 12, color: C.muted }}>—</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 4. NOTIFICATIONS
// ─────────────────────────────────────────────
function Toggle({ on, onChange, disabled }) {
  return (
    <button onClick={() => !disabled && onChange(!on)} disabled={disabled} aria-pressed={on}
      style={{
        width: 44, height: 24, borderRadius: 99, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: on ? C.teal : C.borderM, position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.6 : 1, padding: 0,
      }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20, borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s',
      }}/>
    </button>
  );
}

function Notifications({ learner, onPatch }) {
  const [sms, setSms]     = useState(!!learner.sms_consent);
  const [email, setEmail] = useState(!!learner.email_consent);
  const [busy, setBusy]   = useState(false);

  const toggleSms = async (val) => {
    setSms(val); setBusy(true);
    const err = await onPatch({ sms_consent: val, sms_consent_at: val ? new Date().toISOString() : null });
    if (err) setSms(!val); // revert optimistic flip on failure
    setBusy(false);
  };
  const toggleEmail = async (val) => {
    setEmail(val); setBusy(true);
    const err = await onPatch({ email_consent: val, email_consent_at: val ? new Date().toISOString() : null });
    if (err) setEmail(!val); // revert optimistic flip on failure
    setBusy(false);
  };

  const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.border}` };

  return (
    <div style={sectionStyle}>
      <h2 style={h2Style}>Notifications</h2>
      <div style={rowStyle}>
        <span style={{ fontSize: 14, color: C.text }}>SMS study reminders</span>
        <Toggle on={sms} onChange={toggleSms} disabled={busy} />
      </div>
      <div style={{ ...rowStyle, borderBottom: 'none' }}>
        <span style={{ fontSize: 14, color: C.text }}>Email updates</span>
        <Toggle on={email} onChange={toggleEmail} disabled={busy} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 5. PAUSE SUBSCRIPTION
// ─────────────────────────────────────────────
function PauseSubscription() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [err, setErr]   = useState(null);

  const pause = async () => {
    setBusy(true); setErr(null); setMsg(null);
    const { ok, data } = await postJSON('/api/account', { action: 'pause' });
    setBusy(false);
    if (!ok) {
      setErr(data?.error === 'no_active_subscription' ? 'No active subscription to pause.' : (data?.error || 'Could not pause subscription.'));
      return;
    }
    setMsg('Set to cancel at the end of your billing period — you keep full access until then. Resume anytime before it ends from Manage Subscription.');
    setConfirming(false);
  };

  return (
    <div style={sectionStyle}>
      <h2 style={h2Style}>Pause Subscription</h2>
      <p style={{ fontSize: 13, color: C.dim, margin: '0 0 14px', lineHeight: 1.5 }}>
        Stop your plan from renewing. You keep full access until the end of your current billing period — resume anytime before then from Manage Subscription.
      </p>
      {!confirming ? (
        <button style={btnGhost} onClick={() => { setConfirming(true); setMsg(null); setErr(null); }}>Pause Subscription</button>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={pause}>{busy ? 'Pausing…' : 'Confirm Pause'}</button>
          <button style={btnGhost} onClick={() => setConfirming(false)}>Cancel</button>
        </div>
      )}
      {msg && <p style={{ fontSize: 12, color: C.emerald, fontWeight: 600, margin: '10px 0 0' }}>{msg}</p>}
      {err && <p style={{ fontSize: 12, color: C.red, margin: '10px 0 0' }}>{err}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
// 6. DANGER ZONE
// ─────────────────────────────────────────────
function DangerZone({ onSignOut }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const del = async () => {
    if (confirmText !== 'DELETE') return;
    setBusy(true); setErr(null);
    const { ok, data } = await postJSON('/api/account', { action: 'delete' });
    if (!ok) { setBusy(false); setErr(data?.error || 'Could not delete account.'); return; }
    await supabase.auth.signOut();
    if (onSignOut) onSignOut();
    window.location.replace('/');
  };

  return (
    <div style={{ ...sectionStyle, border: `1px solid ${C.red}` }}>
      <h2 style={{ ...h2Style, color: C.red }}>Danger Zone</h2>
      <p style={{ fontSize: 13, color: C.dim, margin: '0 0 12px', lineHeight: 1.5 }}>
        Deleting your account cancels any active subscription and permanently removes your learning
        data. This cannot be undone. Type <strong style={{ color: C.text }}>DELETE</strong> to confirm.
      </p>
      <div style={{ display: 'flex', gap: 8, maxWidth: 360, marginBottom: 4 }}>
        <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE"
          style={inputStyle} />
        <button onClick={del} disabled={busy || confirmText !== 'DELETE'}
          style={{
            padding: '9px 18px', borderRadius: 9, border: 'none', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
            background: confirmText === 'DELETE' ? C.red : C.elev,
            color: confirmText === 'DELETE' ? '#fff' : C.muted,
            cursor: confirmText === 'DELETE' && !busy ? 'pointer' : 'default',
            opacity: busy ? 0.6 : 1,
          }}>
          {busy ? 'Deleting…' : 'Delete Account'}
        </button>
      </div>
      {err && <p style={{ fontSize: 12, color: C.red, margin: '8px 0 0' }}>{err}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────
export default function AccountPage({ learner: initialLearner, onSignOut, theme, toggleTheme, embedded = false }) {
  const [learner, setLearner] = useState(initialLearner);

  // Load the full learners row (the prop only carries the subset App.jsx
  // resolves — phone, consents, subscription fields come from here).
  useEffect(() => {
    let cancelled = false;
    if (!initialLearner?.id) return;
    getLearner(initialLearner.id).then(row => {
      if (!cancelled && row) setLearner(prev => ({ ...prev, ...row }));
    });
    return () => { cancelled = true; };
  }, [initialLearner?.id]);

  // Patch the learners row and merge into local state. Returns an error string
  // on failure (for inline-field error display), or null on success.
  const patch = async (updates) => {
    const { error } = await updateLearnerProfile(learner.id, updates);
    if (error) return error.message || 'Could not save. Please try again.';
    setLearner(prev => ({ ...prev, ...updates }));
    return null;
  };

  return (
    <div style={embedded
      ? { background: 'transparent', fontFamily: "'Inter','Segoe UI',sans-serif" }
      : { height: '100vh', overflowY: 'auto', background: C.bg, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      {/* Header — only when standalone (/account route). Embedded in the dashboard
          shell, the sidebar already provides nav, so the header is suppressed. */}
      {!embedded && (
        <div style={{ padding: '10px 24px', background: C.panel, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <img src="/logo.jpg" alt="AdaptOS" style={{ height: 84, objectFit: 'contain' }}/>
            <div style={{ width: 1, height: 36, background: C.border }}/>
            <span style={{ fontSize: 20, fontWeight: 800, color: C.text, whiteSpace: 'nowrap' }}>Account</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a href="/" style={{ ...btnGhost, textDecoration: 'none' }}>← Dashboard</a>
            {onSignOut && <button onClick={onSignOut} style={btnGhost}>Sign Out</button>}
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: embedded ? '0 0 40px' : '28px 20px 64px' }}>
        <AccountDetails learner={learner} onPatch={patch} />
        <Subscription />
        <BillingHistory />
        <Notifications learner={learner} onPatch={patch} />
        <PauseSubscription />
        <DangerZone onSignOut={onSignOut} />
      </div>
    </div>
  );
}
